import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  deleteSubscription,
  getAllBillingItems,
  getSubscriptionApiError,
} from "./../../../../../services/subscriptionServices";

import {
  normalizeSubscription,
  normalizeSubscriptions,
} from "../utils/subscriptionNormalizers";

export default function useSubscriptions() {
  const requestIdRef = useRef(0);

  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadSubscriptions = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      setError("");

      try {
        const response = await getAllBillingItems();

        if (requestId !== requestIdRef.current) {
          return;
        }

        setSubscriptions(normalizeSubscriptions(response));
        setHasLoaded(true);
      } catch (loadError) {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setError(
          getSubscriptionApiError(
            loadError,
            "Impossible de charger les abonnements et prestations.",
          ),
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    loadSubscriptions();

    return () => {
      requestIdRef.current += 1;
    };
  }, [loadSubscriptions]);

  const removeSubscription = useCallback(async (id) => {
    setDeletingId(id);

    try {
      await deleteSubscription(id);

      setSubscriptions((currentSubscriptions) =>
        currentSubscriptions.filter(
          (subscription) =>
            Number(subscription.id) !== Number(id),
        ),
      );

      return true;
    } catch (deleteError) {
      throw new Error(
        getSubscriptionApiError(
          deleteError,
          "Impossible de supprimer cet élément.",
        ),
      );
    } finally {
      setDeletingId(null);
    }
  }, []);

  /*
   * À appeler après une création ou une modification réussie.
   */
  const upsertSubscription = useCallback(
    (subscription) => {
      const normalized =
        normalizeSubscription(subscription);

      setSubscriptions((currentSubscriptions) => {
        const existingIndex =
          currentSubscriptions.findIndex(
            (currentSubscription) =>
              Number(currentSubscription.id) ===
              Number(normalized.id),
          );

        if (existingIndex === -1) {
          return [
            normalized,
            ...currentSubscriptions,
          ];
        }

        return currentSubscriptions.map(
          (currentSubscription) =>
            Number(currentSubscription.id) ===
            Number(normalized.id)
              ? normalized
              : currentSubscription,
        );
      });
    },
    [],
  );

  return {
    subscriptions,
    setSubscriptions,

    isLoading,
    isRefreshing,
    hasLoaded,
    error,

    deletingId,

    reloadSubscriptions: loadSubscriptions,
    removeSubscription,
    upsertSubscription,
  };
}